import { formatCurrencyPlain } from '../../../app/currency';

/** Customer-facing quote data lives in order.metadata.quote (merged by PUT /orders/:id). */
export const QUOTE_METADATA_KEY = 'quote';

export const EMPTY_QUOTE_META = { payment_terms: '', customer_notes: '' };

export function readQuoteMeta(order) {
  const raw = order?.metadata?.[QUOTE_METADATA_KEY];
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      obj = null;
    }
  }
  if (!obj || typeof obj !== 'object') return { ...EMPTY_QUOTE_META };
  return {
    payment_terms: typeof obj.payment_terms === 'string' ? obj.payment_terms : '',
    customer_notes: typeof obj.customer_notes === 'string' ? obj.customer_notes : '',
  };
}

/** yyyy-MM-dd… → dd/MM/yyyy without timezone shifts. */
export function formatQuoteDay(value) {
  const s = String(value || '').trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

function itemName(item) {
  const product = String(item.product_name || '').trim();
  const desc = String(item.description || '').trim();
  if (product) return { name: product, details: desc };
  const [first, ...rest] = desc.split('\n');
  return { name: (first || '').trim(), details: rest.join('\n').trim() };
}

function itemAttributes(item) {
  const attrs = item.attributes && typeof item.attributes === 'object' ? item.attributes : {};
  return Object.entries(attrs)
    .filter(([, v]) => String(v || '').trim() !== '')
    .map(([k, v]) => `${k}: ${v}`)
    .join(' · ');
}

/** Flattens a getOrder payload (plus optional customer) into what the client sees. */
export function buildQuoteModel(order, customer = null) {
  const items = [];
  const lines = [...(order?.lines || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  for (const line of lines) {
    for (const it of line.items || []) {
      const qty = Number(it.quantity) || 0;
      const unitPrice = Number(it.unit_price) || 0;
      const { name, details } = itemName(it);
      items.push({
        id: it.id,
        name,
        details,
        attributes: itemAttributes(it),
        quantity: qty,
        unitPrice,
        total: qty * unitPrice,
      });
    }
  }
  const subtotal = items.reduce((s, it) => s + it.total, 0);
  const discount = Math.max(0, Math.min(subtotal, Number(order?.discount_value) || 0));
  const meta = readQuoteMeta(order);
  return {
    number: order?.number || '',
    title: String(order?.title || '').trim(),
    customerName: String(customer?.name || order?.customer_name || '').trim(),
    customerPhone: String(customer?.phone || '').trim(),
    customerEmail: String(customer?.email || '').trim(),
    issuedAt: formatQuoteDay(order?.created_at),
    validUntil: formatQuoteDay(order?.valid_until),
    deliveryDate: formatQuoteDay(order?.delivery_date),
    items,
    subtotal,
    discount,
    total: subtotal - discount,
    paymentTerms: meta.payment_terms.trim(),
    customerNotes: meta.customer_notes.trim(),
  };
}

/** Returns translation keys of what is missing before the quote can be sent to a client. */
export function validateQuoteModel(model) {
  const missing = [];
  if (!model.customerName) missing.push('orders.quote_missing_customer');
  if (!model.title) missing.push('orders.quote_missing_order_title');
  if (!model.validUntil) missing.push('orders.quote_missing_valid_until');
  if (!model.deliveryDate) missing.push('orders.quote_missing_delivery_date');
  if (model.items.length === 0) missing.push('orders.quote_missing_items');
  else if (model.items.some((it) => !it.name)) missing.push('orders.quote_missing_item_name');
  else if (model.items.some((it) => it.quantity <= 0 || it.unitPrice <= 0)) missing.push('orders.quote_missing_item_price');
  if (!model.paymentTerms) missing.push('orders.quote_missing_payment_terms');
  return missing;
}

/** Plain text formatted for WhatsApp (*bold*, _italic_). */
export function buildQuoteWhatsAppText(model, companyName = '') {
  const money = (v) => formatCurrencyPlain(v);
  const out = [];
  out.push(`*Orçamento ${model.number}*${companyName ? ` — ${companyName}` : ''}`);
  if (model.title) out.push(model.title);
  out.push('');
  if (model.customerName) out.push(`Olá, ${model.customerName.split(' ')[0]}! Segue o orçamento solicitado:`);
  out.push('');
  model.items.forEach((it, i) => {
    out.push(`${i + 1}. *${it.name}*`);
    if (it.attributes) out.push(`   ${it.attributes}`);
    if (it.details) it.details.split('\n').forEach((l) => l.trim() && out.push(`   _${l.trim()}_`));
    out.push(`   ${it.quantity} × ${money(it.unitPrice)} = ${money(it.total)}`);
  });
  out.push('');
  if (model.discount > 0) {
    out.push(`Subtotal: ${money(model.subtotal)}`);
    out.push(`Desconto: -${money(model.discount)}`);
  }
  out.push(`*Total: ${money(model.total)}*`);
  out.push('');
  if (model.paymentTerms) out.push(`💳 *Pagamento:* ${model.paymentTerms}`);
  if (model.deliveryDate) out.push(`📦 *Previsão de entrega:* ${model.deliveryDate}`);
  if (model.validUntil) out.push(`📅 *Válido até:* ${model.validUntil}`);
  if (model.customerNotes) {
    out.push('');
    out.push(model.customerNotes);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
